import { z } from 'zod'

import {
  EMAIL_MAX_LENGTH,
  isEmailLocalPartTooLong,
} from '@/utils/validate-email'

const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/
const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/

export const loginFormValidationSchema = z.object({
  email: z
    .string()
    .min(1, 'Insira seu endereço de email.')
    .email('Insira um endereço de email válido.')
    .max(
      EMAIL_MAX_LENGTH,
      `O endereço de email deve ter no máximo ${EMAIL_MAX_LENGTH} caracteres.`,
    ),
  password: z
    .string()
    .min(1, 'Insira sua senha de acesso.')
    .max(255, 'A senha deve ter no máximo 255 caracteres'),
})

export type LoginFormValues = z.infer<typeof loginFormValidationSchema>

export const registerPreserverFormValidationSchema = z
  .object({
    nome: z
      .string()
      .min(1, 'O nome é obrigatório')
      .max(255, 'O nome deve ter no máximo 255 caracteres'),
    phone: z
      .string()
      .min(1, 'O telefone é obrigatório')
      .regex(
        /^\(?\d{2}\)?[-.\s]?\d{4,5}[-.\s]?\d{4}$/,
        'Digite um telefone válido',
      ),
    email: z
      .string()
      .min(1, 'O e-mail é obrigatório')
      .max(
        EMAIL_MAX_LENGTH,
        `O endereço de email deve ter no máximo ${EMAIL_MAX_LENGTH} caracteres.`,
      )
      .email('Digite um e-mail válido')
      .regex(
        /^[a-zA-Z0-9.$/-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'O e-mail contém caracteres inválidos',
      )
      .superRefine((email, ctx) => {
        const { isTooLong, maxLength } = isEmailLocalPartTooLong({
          email,
        })

        if (isTooLong) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `O email antes do @ deve ter no máximo ${maxLength} caracteres.`,
          })
        }
      }),
    documento: z.string().min(1, 'Esse campo é obrigatório'),
    password: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    confirmPassword: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    tp_cadastro: z.enum(['F', 'P']).default('F'),
    tp_perfil: z.number().default(1),
  })
  .refine(
    (data) => {
      const documento = data.documento
      const tpCadastro = data.tp_cadastro

      if (tpCadastro === 'F') {
        return cpfRegex.test(documento)
      }
      return cnpjRegex.test(documento)
    },

    {
      message: 'Documento inválido',
      path: ['documento'],
    },
  )
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  })

export type RegisterPreserverFormValues = z.infer<
  typeof registerPreserverFormValidationSchema
>
export const registerAgentFormValidationSchema = z
  .object({
    nome: z
      .string()
      .min(1, 'O nome é obrigatório')
      .max(255, 'O nome deve ter no máximo 255 caracteres'),
    phone: z
      .string()
      .min(1, 'O telefone é obrigatório')
      .regex(
        /^\(?\d{2}\)?[-.\s]?\d{4,5}[-.\s]?\d{4}$/,
        'Digite um telefone válido',
      ),
    email: z
      .string()
      .min(1, 'O e-mail é obrigatório')
      .max(
        EMAIL_MAX_LENGTH,
        `O endereço de email deve ter no máximo ${EMAIL_MAX_LENGTH} caracteres.`,
      )
      .email('Digite um e-mail válido')
      .regex(
        /^[a-zA-Z0-9.$/-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'O e-mail contém caracteres inválidos',
      )
      .superRefine((email, ctx) => {
        const { isTooLong, maxLength } = isEmailLocalPartTooLong({
          email,
        })

        if (isTooLong) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `O email antes do @ deve ter no máximo ${maxLength} caracteres.`,
          })
        }
      }),
    documento: z.string().min(1, 'Esse campo é obrigatório'),
    password: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    confirmPassword: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    tp_cadastro: z.enum(['F', 'P']).default('F'),
    tp_perfil: z.number().default(5),
    classificacao_agente: z.number().default(1),
  })
  .refine(
    (data) => {
      const documento = data.documento
      const tpCadastro = data.tp_cadastro

      if (tpCadastro === 'F') {
        return cpfRegex.test(documento)
      }
      return cnpjRegex.test(documento)
    },

    {
      message: 'Documento inválido',
      path: ['documento'],
    },
  )
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  })

export type RegisterAgentFormValues = z.infer<
  typeof registerAgentFormValidationSchema
>

export const registerSponsorFormValidationSchema = z
  .object({
    nome: z
      .string()
      .min(1, 'O nome é obrigatório')
      .max(255, 'O nome deve ter no máximo 255 caracteres'),
    phone: z
      .string()
      .min(1, 'O telefone é obrigatório')
      .regex(
        /^\(?\d{2}\)?[-.\s]?\d{4,5}[-.\s]?\d{4}$/,
        'Digite um telefone válido',
      ),
    email: z
      .string()
      .min(1, 'O e-mail é obrigatório')
      .max(
        EMAIL_MAX_LENGTH,
        `O endereço de email deve ter no máximo ${EMAIL_MAX_LENGTH} caracteres.`,
      )
      .email('Digite um e-mail válido')
      .regex(
        /^[a-zA-Z0-9.$/-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'O e-mail contém caracteres inválidos',
      )
      .superRefine((email, ctx) => {
        const { isTooLong, maxLength } = isEmailLocalPartTooLong({
          email,
        })

        if (isTooLong) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `O email antes do @ deve ter no máximo ${maxLength} caracteres.`,
          })
        }
      }),
    documento: z
      .string()
      .min(1, 'Esse campo é obrigatório')
      .max(50, 'O documento deve ter no máximo 50 caracteres'),
    password: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    confirmPassword: z
      .string()
      .min(8, 'A senha deve conter no mínimo 8 caracteres')
      .max(50, 'A senha deve conter no máximo 50 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'A senha deve conter pelo menos um caractere especial',
      )
      .regex(/^\S*$/, 'A senha não deve conter espaços'),
    tp_cadastro: z.enum(['F', 'P']).default('F'),
    nacionalidade: z.enum(['Brasileiro', 'Estrangeiro']).default('Brasileiro'),
    tp_perfil: z.number().default(2),
  })
  .refine(
    (data) => {
      const documento = data.documento
      const tpCadastro = data.tp_cadastro
      const nacionalidade = data.nacionalidade

      if (nacionalidade === 'Estrangeiro') return true

      if (tpCadastro === 'F') {
        return cpfRegex.test(documento)
      }
      return cnpjRegex.test(documento)
    },
    {
      message: 'Documento inválido',
      path: ['documento'],
    },
  )
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  })

export type RegisterSponsorFormValues = z.infer<
  typeof registerSponsorFormValidationSchema
>
